import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, Navigate, useLocation, useNavigate } from "react-router";

import type { InviteInfo, LoginStep, Session } from "@/auth/types";
import { AuthError } from "@/auth/types";
import { useAuth } from "@/auth/use-auth";
import { LoadingState } from "@/components/EmptyState";

import { AuthLayout, FormAlert, useAuthErrorText } from "./components";
import { EnrollStep } from "./EnrollStep";
import { PasswordChangeForm } from "./PasswordChangeForm";
import { TotpStep } from "./TotpStep";

/**
 * The invitation link keeps its token in the fragment (`/invite#token=…` — never sent to a server,
 * so it is not in any access log); `?token=` works as well. The token is taken out of the address
 * bar at once so it does not stay in the browser history.
 */
function inviteTokenFrom(location: { hash: string; search: string }): string {
  const fromHash = new URLSearchParams(location.hash.replace(/^#/, "")).get("token");
  return fromHash ?? new URLSearchParams(location.search).get("token") ?? "";
}

type Stage =
  | { kind: "checking" }
  /** `reason`: a missing token, the service's answer, or a plain message key */
  | { kind: "invalid"; reason: "missing" | "sign_in_now" | { error: unknown } }
  | { kind: "password"; invite: InviteInfo }
  | Exclude<LoginStep, { kind: "session" | "password_change" }>;

/**
 * `/invite` (T01-18): the link from the invitation e-mail → "choose your password" → two-step
 * set-up for owners/managers (the same steps as the first sign-in) → the admin, signed in.
 * A used or expired link says so in plain words and points at the owner ("send it again").
 */
export function AcceptInvitePage() {
  const { t, i18n } = useTranslation("auth");
  const { state, acceptSession, api } = useAuth();
  const errorText = useAuthErrorText();
  const location = useLocation();
  const navigate = useNavigate();
  const [token] = useState(() => inviteTokenFrom(location));
  const [stage, setStage] = useState<Stage>(() =>
    token.length < 16 ? { kind: "invalid", reason: "missing" } : { kind: "checking" },
  );
  const [signedIn, setSignedIn] = useState(false);

  const hasSecretInUrl = Boolean(location.hash || location.search);
  useEffect(() => {
    if (hasSecretInUrl) {
      void navigate(location.pathname, { replace: true });
    }
  }, [hasSecretInUrl, navigate, location.pathname]);

  useEffect(() => {
    if (token.length < 16) {
      return;
    }
    let live = true;
    api.checkInvite(token).then(
      (invite) => {
        if (live) {
          setStage((current) =>
            current.kind === "checking" ? { kind: "password", invite } : current,
          );
        }
      },
      (error: unknown) => {
        if (live) {
          setStage({ kind: "invalid", reason: { error } });
        }
      },
    );
    return () => {
      live = false;
    };
  }, [api, token]);

  if (signedIn && state.status === "authenticated") {
    return <Navigate to="/" replace />;
  }

  const finish = (session: Session) => {
    setSignedIn(true);
    acceptSession(session);
  };

  if (stage.kind === "checking") {
    return (
      <AuthLayout title={t("invite.title")}>
        <LoadingState />
      </AuthLayout>
    );
  }

  if (stage.kind === "invalid") {
    const { reason } = stage;
    const message =
      reason === "missing"
        ? t("invite.missing")
        : reason === "sign_in_now"
          ? t("invite.sign_in_now")
          : reason.error instanceof AuthError && reason.error.key === "challenge_expired"
            ? t("invite.used_or_expired")
            : errorText(reason.error);
    return (
      <AuthLayout title={t("invite.title")}>
        <FormAlert message={message} />
        <Link to="/login" className="min-h-touch self-start py-2 underline underline-offset-4">
          {t("invite.to_login")}
        </Link>
      </AuthLayout>
    );
  }

  if (stage.kind === "enroll") {
    return (
      <AuthLayout title={t("setup.title")}>
        <EnrollStep
          start={() => api.startEnrollment(stage.enrollToken)}
          confirm={(code) => api.confirmEnrollment(stage.enrollToken, code)}
          onDone={(result) => {
            if (result.session) {
              finish(result.session);
            } else {
              setStage({ kind: "invalid", reason: "sign_in_now" });
            }
          }}
        />
      </AuthLayout>
    );
  }

  if (stage.kind === "mfa") {
    return (
      <AuthLayout title={t("totp.title")}>
        <TotpStep
          onSubmit={async (code) => {
            finish(await api.verifyMfa(stage.mfaToken, code));
          }}
          onBack={() => {
            setStage({ kind: "invalid", reason: "sign_in_now" });
          }}
        />
      </AuthLayout>
    );
  }

  const { invite } = stage;
  const until =
    invite.expiresAt === null
      ? null
      : new Intl.DateTimeFormat(i18n.resolvedLanguage ?? "en", {
          dateStyle: "medium",
          timeStyle: "short",
          timeZone: "Asia/Tokyo",
        }).format(invite.expiresAt);
  return (
    <AuthLayout
      title={t("invite.title")}
      intro={t("invite.intro", { name: invite.name, email: invite.email })}
    >
      <div className="flex flex-col gap-4" data-page="accept-invite">
        {invite.requiresTotp ? <p className="text-sm">{t("invite.two_step")}</p> : null}
        {until ? (
          <p className="text-sm text-muted-foreground">{t("invite.valid_until", { until })}</p>
        ) : null}
        <PasswordChangeForm
          withCurrent={false}
          submitLabel={t("invite.submit")}
          onSubmit={async ({ newPassword }) => {
            try {
              const next = await api.acceptInvite(token, newPassword);
              if (next.kind === "session") {
                finish(next.session);
              } else if (next.kind === "password_change") {
                setStage({ kind: "invalid", reason: "sign_in_now" });
              } else {
                setStage(next);
              }
            } catch (error) {
              if (error instanceof AuthError && error.key === "challenge_expired") {
                setStage({ kind: "invalid", reason: { error } });
                return;
              }
              throw error;
            }
          }}
        />
      </div>
    </AuthLayout>
  );
}
